import { useApolloClient } from '@apollo/client';
import { isNil, omit } from 'lodash';
import { useCallback } from 'react';
import { Mutations } from '@shared/api';
import { API } from '@shared/types/api';
import { toComponentOneOf } from '@shared/util/workflow';
import {
  queryWorkflowTemplateComponents,
  refetchAndInvalidateQueriesAfterMutatingViewTemplates,
  withoutUnknownComponents,
} from '../utils';

export function useUpdateViewTemplate(workflowTemplateId?: string | null) {
  const apollo = useApolloClient();

  return useCallback(
    async (viewTemplate: Partial<API.ViewTemplateFragment> & { id: string }) => {
      const mutationPreparedViewTemplate = omit(
        viewTemplate,
        'cardUITemplate',
        'createdAt',
        'updatedAt',
      ) as API.ViewTemplateFragment;

      const componentsViewOptions = isNil(workflowTemplateId)
        ? viewTemplate.componentsViewOptions
        : withoutUnknownComponents(
            viewTemplate.componentsViewOptions,
            await queryWorkflowTemplateComponents(apollo, workflowTemplateId),
          );

      return apollo.mutate<API.UpdateViewTemplateMutation, API.UpdateViewTemplateMutationVariables>(
        {
          mutation: Mutations.viewTemplate.updateViewTemplate,
          variables: {
            input: {
              ...mutationPreparedViewTemplate,
              defaultReadonlyComponents:
                mutationPreparedViewTemplate.type === 'COLLECTION' ? true : false,
              componentsViewOptions: componentsViewOptions?.map(viewOption => {
                return {
                  componentId: viewOption.componentId,
                  isHidden: viewOption.isHidden,
                  overrides: !isNil(viewOption.overrides)
                    ? toComponentOneOf(viewOption.overrides)
                    : null,
                };
              }),
              workflowTemplateId: workflowTemplateId ?? '',
            },
          },
          ...refetchAndInvalidateQueriesAfterMutatingViewTemplates(workflowTemplateId ?? null),
        },
      );
    },
    [apollo, workflowTemplateId],
  );
}
